import { Badge, Button, EmptyState, Row, TextField } from "@community-project-org/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Plus } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import { Money } from "../../common/components/Money.js";
import type { components } from "../../generated/api.js";
import { PlanSheet } from "./PlanSheet.js";

type MemberPlanDTO = components["schemas"]["MemberPlanDTO"];
type MemberPassDTO = components["schemas"]["MemberPassDTO"];
type AccessStatsDTO = components["schemas"]["AccessStatsDTO"];
type ServiceGroupDTO = components["schemas"]["ServiceGroupDTO"];
type ServiceDTO = components["schemas"]["ServiceDTO"];

const PASS_STATUS: Record<MemberPassDTO["status"], { label: string; tone?: "warning" | "danger" }> = {
  ACTIVE: { label: "aktiv" },
  CANCELLED: { label: "gekündigt", tone: "danger" },
};

const dateFormat = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" });

/**
 * Mitgliedschaften im Backoffice: Stufen mit ihren Regeln, darunter die ausgestellten
 * Ausweise. Ausgestellt wird per Mailadresse — wer noch kein Konto hat, bekommt den
 * Ausweis, sobald er sich mit dieser Adresse anmeldet.
 */
export function MembersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const plans = useQuery({ queryKey: ["member-plans"], queryFn: () => apiRequest<MemberPlanDTO[]>("/members/plans") });
  const passes = useQuery({ queryKey: ["member-passes"], queryFn: () => apiRequest<MemberPassDTO[]>("/members/passes") });
  const groups = useQuery({ queryKey: ["service-groups"], queryFn: () => apiRequest<ServiceGroupDTO[]>("/service-groups") });
  const services = useQuery({ queryKey: ["services"], queryFn: () => apiRequest<ServiceDTO[]>("/services") });
  const access = useQuery({
    queryKey: ["member-access"],
    queryFn: () => apiRequest<AccessStatsDTO>("/members/access?days=30"),
    refetchInterval: 60_000,
  });
  const [editing, setEditing] = useState<MemberPlanDTO | "new" | null>(null);
  const [email, setEmail] = useState("");
  const [planId, setPlanId] = useState("");

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["member-passes"] }),
      queryClient.invalidateQueries({ queryKey: ["member-plans"] }),
    ]);
  };
  const issue = useMutation({
    mutationFn: () => apiRequest<MemberPassDTO>("/members/passes", { method: "POST", body: { email, planId } }),
    onSuccess: async () => {
      setEmail("");
      await refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) =>
      apiRequest<MemberPassDTO>(`/members/passes/${encodeURIComponent(id)}/cancel`, { method: "PATCH" }),
    onSuccess: refresh,
  });

  const activePlans = plans.data?.filter((plan) => !plan.archived) ?? [];
  const chosenPlan = planId || activePlans[0]?.id || "";

  return (
    <div className="px-5 pb-8">
      <div className="flex items-center gap-3 pt-5">
        <h1 className="flex-1 text-[22px] font-extrabold tracking-[-0.03em] text-ink">Mitgliedschaften</h1>
        <Button onClick={() => setEditing("new")}>
          <Plus size={16} aria-hidden="true" />
          Stufe
        </Button>
      </div>

      {plans.isPending && <p className="mt-5 text-[14px] text-ink-3">Wird geladen …</p>}
      {plans.data?.length === 0 && (
        <EmptyState
          icon={<BadgeCheck aria-hidden="true" />}
          title="Noch keine Stufe"
          description="Eine Stufe legt fest, was Mitglieder bekommen: Rabatt, freie Buchungen oder früheren Vorverkauf."
        />
      )}
      {plans.data && plans.data.length > 0 && (
        <div className="card mt-5 divide-y divide-line">
          {plans.data.map((plan) => (
            <Row
              key={plan.id}
              title={
                <span className="flex flex-wrap items-center gap-2">
                  {plan.name}
                  {plan.archived && <Badge>archiviert</Badge>}
                </span>
              }
              subtitle={
                <span>
                  <Money cents={plan.priceCents} currency={plan.currency} />
                  {plan.durationDays ? ` · ${plan.durationDays} Tage` : " · unbefristet"}
                  {` · ${plan.rules.length} ${plan.rules.length === 1 ? "Regel" : "Regeln"} · `}
                  <span className="num">{plan.activePasses}</span> aktiv
                </span>
              }
              chevron
              onSelect={() => setEditing(plan)}
            />
          ))}
        </div>
      )}

      {access.data && (
        <div className="card mt-5 grid grid-cols-2 gap-3 p-4">
          <div>
            <p className="micro">Gerade da</p>
            <p className="num text-[22px] font-extrabold text-ink">{access.data.present}</p>
          </div>
          <div>
            <p className="micro">Check-ins, 30 Tage</p>
            <p className="num text-[22px] font-extrabold text-ink">
              {access.data.days.reduce((sum, day) => sum + day.checkIns, 0)}
            </p>
          </div>
        </div>
      )}

      <h2 className="micro mt-8">Ausweise</h2>
      {activePlans.length > 0 && (
        <form
          className="card mt-3 grid gap-3 p-4 sm:grid-cols-[1fr_auto_auto] sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            issue.mutate();
          }}
        >
          <TextField label="Mailadresse" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
          <label className="grid gap-1 text-[12px] font-bold text-ink-2">
            Stufe
            <select
              className="min-h-[44px] rounded-md border border-line bg-surface px-2 text-[14px] text-ink"
              value={chosenPlan}
              onChange={(event) => setPlanId(event.target.value)}
            >
              {activePlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" disabled={issue.isPending || !chosenPlan} aria-busy={issue.isPending}>
            {issue.isPending ? "Wird ausgestellt …" : "Ausstellen"}
          </Button>
          {issue.error && (
            <p className="text-[13px] text-danger sm:col-span-3" role="alert">
              {issue.error instanceof ApiRequestError ? issue.error.message : "Das hat nicht geklappt."}
            </p>
          )}
        </form>
      )}

      {passes.data?.length === 0 && <p className="mt-3 text-[14px] text-ink-3">Noch keine Ausweise ausgestellt.</p>}
      {passes.data && passes.data.length > 0 && (
        <div className="card mt-3 divide-y divide-line">
          {passes.data.map((pass) => {
            const status = PASS_STATUS[pass.status];
            return (
              <div key={pass.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-[15px] font-bold text-ink">
                    {pass.holderName}
                    <Badge {...(status.tone ? { tone: status.tone } : {})}>{status.label}</Badge>
                  </p>
                  <p className="truncate text-[13px] text-ink-2">
                    {pass.planName} · {pass.holderEmail}
                    {pass.validUntil ? ` · bis ${dateFormat.format(new Date(pass.validUntil))}` : ""}
                  </p>
                </div>
                {pass.status === "ACTIVE" && (
                  <Button
                    variant="secondary"
                    disabled={cancel.isPending && cancel.variables === pass.id}
                    aria-busy={cancel.isPending && cancel.variables === pass.id}
                    onClick={() => cancel.mutate(pass.id)}
                  >
                    Kündigen
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <PlanSheet
          plan={editing === "new" ? null : editing}
          groups={groups.data ?? []}
          services={services.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
